import React from 'react';
import {
  Grid,
  Card,
  Flex,
  Text,
  HStack,
  Badge,
  InfoTooltip,
  Circle,
  Divider,
} from '@wegroup/design-system';
import styled from 'styled-components';
import CheckMark from '../../../../../../common/src/components/CheckMark';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import TextError from '../../../ui/advisory-flow/components/shared-components/TextError';
import { handleResponseLanguage } from '../../../../../../../i18n';

interface FixedCoverageFeature {
  title: Record<string, string>;
  info?: string | null;
}

interface FixedCoverageOption {
  value: string;
  label: Record<string, string>;
  icon_key?: string | null;
  subtitle?: string | null;
  description?: string | null;
  features?: FixedCoverageFeature[];
}

interface Props {
  question: FlowsQuestion;
}

/**
 * Input component for questions with `rules.input_type === "COVERAGE"`.
 * Each enum option is rendered as a selectable card containing the plan title, an optional price badge
 * (extracted from the description), and its list of features.
 */
const FixedCoveragesInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const {
    field: { value: selectedValue, onBlur },
    error,
  } = useFlowInputComponent({ question });

  // Cast the enum to include the additional feature fields we need for rendering.
  const options: FixedCoverageOption[] = (question.rules.enum ||
    []) as FixedCoverageOption[];

  const handleSelect = (val: string) => {
    if (val === selectedValue) {
      // Deselect when clicking again on the already-selected card
      onBlur({ name: question.id, value: undefined });
    } else {
      onBlur({ name: question.id, value: val });
    }
  };

  const getPriceFromDescription = (
    desc?: string | Record<string, string> | null,
  ): string | undefined => {
    // Handle both string and object descriptions
    const descText =
      typeof desc === 'string' ? desc : handleResponseLanguage(desc);
    if (!descText || typeof descText !== 'string') return undefined;
    // The backend description looks like "€164.70 per month • ..." – take the first segment before the first bullet.
    const parts = descText.split('•');
    return parts[0]?.trim();
  };

  return (
    <>
      <TextError error={error?.message} />
      <Grid
        templateColumns={{ base: '1fr', tablet: 'repeat(2, 1fr)' }}
        gridGap="2.4rem"
        width="100%"
        justifyContent="center"
        alignItems="stretch"
      >
        {options.map((opt) => {
          const isSelected = selectedValue === opt.value;

          // Resolve translations. Fallback to raw value when not provided.
          const title = handleResponseLanguage(opt.label) || opt.value;
          const price = getPriceFromDescription(opt.description);
          const subtitle = handleResponseLanguage(opt.subtitle) || opt.subtitle;
          const features = opt.features || [];

          return (
            <CardWrapper
              key={opt.value}
              checked={isSelected}
              onClick={() => handleSelect(opt.value)}
              data-test-id="ProdFac_FixedCoveragesInputComponent_Card"
            >
              {/* Radio-style selection indicator */}
              <Circle
                size="2rem"
                border="2px solid"
                borderColor={isSelected ? 'orange.500' : 'gray.300'}
                bg={isSelected ? 'orange.500' : 'transparent'}
                position="absolute"
                top="1.6rem"
                right="1.6rem"
                display="flex"
                alignItems="center"
                justifyContent="center"
                data-test-id="ProdFac_FixedCoveragesInputComponent_RadioIndicator"
              >
                {isSelected && <Circle size="0.8rem" bg="white" />}
              </Circle>

              <Flex
                direction="column"
                align="flex-start"
                gap="1.6rem"
                w="100%"
                h="100%"
              >
                {/* Header – title and price badge stacked */}
                <Flex
                  direction="column"
                  align="flex-start"
                  gap="0.8rem"
                  w="100%"
                >
                  <Text textStyle="largeBodyBold">{title}</Text>
                  {price && (
                    <Badge
                      colorScheme="primary"
                      px="3"
                      py="1"
                      borderRadius="full"
                    >
                      {price}
                    </Badge>
                  )}
                  {subtitle && (
                    <Text
                      textStyle="smallBody"
                      color="gray.500"
                      whiteSpace="pre-line"
                    >
                      {subtitle}
                    </Text>
                  )}
                </Flex>

                {/* Subtle divider */}
                {price && <Divider borderColor="gray.200" opacity={0.2} />}

                {/* Feature list */}
                <Flex direction="column" gap="0.8rem" w="100%">
                  {features.map((feat, idx) => {
                    const featTitle = handleResponseLanguage(feat.title);
                    if (!featTitle) return null;
                    return (
                      <HStack
                        key={idx}
                        align="flex-start"
                        spacing="0.8rem"
                        data-test-id="ProdFac_FixedCoveragesInputComponent_Feature"
                      >
                        <CheckMark size="4" />
                        <Text>{featTitle}</Text>
                        {feat.info && (
                          <InfoTooltip
                            label={
                              typeof feat.info === 'string'
                                ? feat.info
                                : handleResponseLanguage(feat.info)
                            }
                            boxSize={5}
                          />
                        )}
                      </HStack>
                    );
                  })}
                </Flex>
              </Flex>
            </CardWrapper>
          );
        })}
      </Grid>
    </>
  );
};

const CardWrapper = styled(Card)<{ checked?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 2.4rem;
  border: ${({ checked, theme }) =>
    checked ? `2px solid ${theme.primary[500]}` : '1px solid #E0E0E0'};
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  min-height: 100%;
  height: 100%;
  &:hover {
    background: #fafafa;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

export default FixedCoveragesInputComponent;
